import { useCallback, useEffect } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import { statusToStr, strToStatus } from '@/_schemas/status-input';
import { useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useDeleteMutation, useGetJobPosition, useUpdateMutation } from './apis';
import type { JobPositionFormSchema, JobPositionQuerySchema, JobPositionUpdateSchema } from './schemas';
import { useFormDefinition } from './use-form';

export function usePage() {
  const nav = useNavigate();
  const edit = useEdit();
  const form = useFormDefinition();

  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'job-positions', id: `${id}` });

  const jobPosition = useGetJobPosition(id ?? '');
  const { setHasToInvalidate, removeQuery } = useInvalidate(['JOB_POSITION', id ?? '']);
  const updateMutation = useUpdateMutation(id ?? '');
  const deleteMutation = useDeleteMutation();
  const formToMutation = useCallback(
    ({
      status,
      preferred_relationship,
      preferred_location,
      business_discipline,
      salary_grade,
      department,
      ...rest
    }: JobPositionFormSchema): JobPositionUpdateSchema => ({
      ...rest,
      status: statusToStr(status),
      preferred_relationship_id: preferred_relationship?.id || null,
      preferred_location_id: preferred_location?.id || null,
      business_discipline_id: business_discipline?.id || null,
      department_id: department?.id || null,
      salary_grade_id: salary_grade?.id || null,
    }),
    []
  );

  const { onUpdate, onDelete, isDeleting, isPending, isSuccess } = useProfileCrudActions<
    JobPositionFormSchema,
    JobPositionUpdateSchema,
    { data: JobPositionQuerySchema }
  >({
    form,
    edit,
    formToMutation,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Job Position has been updated.',
    deleteSuccessMessage: 'Job Position has been deleted.',
    successToastId: '',
    onSuccessSideEffects: () => {
      removeQuery(['JOB_POSITIONS']);
      setHasToInvalidate.setTrue();
    },
    onDeleteSuccess: () => {
      nav({ to: '/settings/job-positions' });
    },
  });

  const onSubmit = useCallback(
    (values: JobPositionFormSchema) => {
      onUpdate(formToMutation(values));
    },
    [formToMutation, onUpdate]
  );

  useEffect(() => {
    const { data: response } = jobPosition;
    if (response) {
      const { data } = response;

      form.reset({
        ...data,
        id: data.id,
        status: strToStatus(data.status),
        result_statement: data.result_statement || '',
        strategic_work: data.strategic_work || '',
        tactical_work: data.tactical_work || '',
        standards_work: data.standards_work || '',

        need_to_succeed: data.need_to_succeed || '',
        expectations: data.expectations || '',
        position_overview: data.position_overview || '',
        applicant_questions: data.applicant_questions || '',
        interview_questions: data.interview_questions || '',

        preferred_relationship: data.preferred_relationship || null,
        preferred_location: data.preferred_location || null,
        business_discipline: data.business_discipline || null,
        department: data.department || null,
        salary_grade: data.salary_grade || null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition.data]);

  console.log(form.formState.errors);

  return { form, isSuccess, isPending, onSubmit, edit, onDelete, isDeleting };
}

export type UsePageType = ReturnType<typeof usePage>;
